import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, X } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";

import { ApiRequestError, apiRequest } from "../../common/api/apiClient.js";
import { ROLE_LABELS, type TenantRole } from "../../common/membership/roles.js";
import type { components } from "../../generated/api.js";

type PlatformUserDTO = components["schemas"]["PlatformUserDTO"];
type PlatformTenantDTO = components["schemas"]["PlatformTenantDTO"];

const ROLLEN: TenantRole[] = [
  "CONTROLLER",
  "SUPPORT",
  "SERVICE_EDITOR",
  "MANAGER",
  "TENANT_ADMIN",
  "PLATFORM_ADMIN",
];

/**
 * Konten plattformweit: sperren, entsperren, Rollen vergeben und entziehen.
 *
 * Eine Rolle lässt sich auf eine Mailadresse ausstellen, hinter der sich noch niemand
 * angemeldet hat — sie liegt dann bereit und greift beim ersten Login. Sonst müsste
 * jeder Neue erst einmal anklopfen und warten.
 */
export function AdminUsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [role, setRole] = useState<TenantRole>("CONTROLLER");

  const users = useQuery({
    queryKey: ["platform-users"],
    queryFn: () => apiRequest<PlatformUserDTO[]>("/platform/users"),
  });
  const tenants = useQuery({
    queryKey: ["platform-tenants"],
    queryFn: () => apiRequest<PlatformTenantDTO[]>("/platform/tenants"),
  });

  const aktualisieren = () => queryClient.invalidateQueries({ queryKey: ["platform-users"] });

  const status = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "BLOCKED" }) =>
      apiRequest<PlatformUserDTO>(`/platform/users/${id}/status`, {
        method: "POST",
        body: { status },
      }),
    onSuccess: aktualisieren,
  });

  const vergeben = useMutation({
    mutationFn: () =>
      apiRequest<PlatformUserDTO>("/platform/memberships", {
        method: "POST",
        body: { email, tenantId: tenantId || tenants.data?.[0]?.id, role },
      }),
    onSuccess: async () => {
      setEmail("");
      await aktualisieren();
    },
  });

  const entziehen = useMutation({
    mutationFn: ({ userId, tenantId }: { userId: string; tenantId: string }) =>
      apiRequest<PlatformUserDTO>(`/platform/users/${userId}/memberships/${tenantId}`, {
        method: "DELETE",
      }),
    onSuccess: aktualisieren,
  });

  const liste = users.data ?? [];
  const fehler = [status.error, vergeben.error, entziehen.error].find(Boolean);

  return (
    <div className="px-5 pb-8">
      <h1 className="pt-5 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Konten</h1>
      <p className="mt-1 max-w-[65ch] text-[13.5px] leading-relaxed text-ink-2">
        Die Identität liegt im gemeinsamen Konto von Community Project. Hier wird
        entschieden, wer in BookKing was darf — und wer gar nicht mehr hereinkommt.
      </p>

      <form
        className="mt-4 flex flex-wrap items-end gap-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim().length > 0) {
            vergeben.mutate();
          }
        }}
      >
        <div className="min-w-[14rem] flex-1">
          <label className="micro block" htmlFor="rolle-email">
            Rolle vergeben an
          </label>
          <input
            id="rolle-email"
            type="email"
            className="input mt-1.5"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="kinomitarbeiter@bookking.test"
          />
        </div>
        <div>
          <label className="micro block" htmlFor="rolle-tenant">
            Unternehmen
          </label>
          <select
            id="rolle-tenant"
            className="input mt-1.5"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
          >
            {(tenants.data ?? []).map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="micro block" htmlFor="rolle-rolle">
            Rolle
          </label>
          <select
            id="rolle-rolle"
            className="input mt-1.5"
            value={role}
            onChange={(event) => setRole(event.target.value as TenantRole)}
          >
            {ROLLEN.map((entry) => (
              <option key={entry} value={entry}>
                {ROLE_LABELS[entry].label}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn" disabled={vergeben.isPending}>
          Vergeben
        </button>
      </form>

      {fehler && (
        <p className="mt-3 text-[13px] text-danger">
          {fehler instanceof ApiRequestError ? fehler.message : "Die Aktion ist fehlgeschlagen."}
        </p>
      )}

      {users.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}

      <ul className="mt-5 flex flex-col gap-2.5">
        {liste.map((user) => {
          const gesperrt = user.status === "BLOCKED";
          return (
            <li key={user.id} className="card pad">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold text-ink">{user.displayName}</p>
                  <p className="truncate text-[13px] text-ink-2">{user.email}</p>
                </div>
                <span className={gesperrt ? "st st-cancelled" : "st st-confirmed"}>
                  {gesperrt ? "Gesperrt" : "Aktiv"}
                </span>
                {/* Vorbereitet heisst: die Rolle liegt bereit, angemeldet hat sich
                    dahinter noch niemand. */}
                {!user.activated && <span className="st st-created">Vorbereitet</span>}
                <button
                  type="button"
                  className="grid h-9 w-9 place-items-center rounded-sm text-ink-3 hover:text-ink"
                  onClick={() =>
                    status.mutate({ id: user.id, status: gesperrt ? "ACTIVE" : "BLOCKED" })
                  }
                  aria-label={gesperrt ? "Entsperren" : "Sperren"}
                  title={gesperrt ? "Entsperren" : "Sperren"}
                >
                  {gesperrt ? <Check size={16} /> : <Ban size={16} />}
                </button>
              </div>

              {user.memberships.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {user.memberships.map((entry) => (
                    <li key={entry.tenantId}>
                      <span className="chip">
                        {entry.tenantName} · {ROLE_LABELS[entry.role].label}
                        <button
                          type="button"
                          className="ml-0.5 text-ink-3 hover:text-danger"
                          onClick={() =>
                            entziehen.mutate({ userId: user.id, tenantId: entry.tenantId })
                          }
                          aria-label={`Rolle in ${entry.tenantName} entziehen`}
                        >
                          <X size={13} aria-hidden="true" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
